import { copy } from '@ohear/shared'
import {
  Avatar,
  Badge,
  Button,
  Card,
  Checkbox,
  Input,
  Spinner,
  Textarea,
  useToast,
} from '@ohear/ui'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { AccountTabs } from '../../features/account/AccountTabs'
import { ChipMultiSelect, type ChipOption } from '../../features/account/ChipMultiSelect'
import { ImageUpload } from '../../features/account/ImageUpload'
import { PhoneInput } from '../../features/account/PhoneInput'
import { PhoneVerifyModal } from '../../features/account/PhoneVerifyModal'
import { type UnionStatus, UnionStatusGroup } from '../../features/account/UnionStatusGroup'
import {
  type AccountProfile,
  type AccountProfileUpdate,
  type ActorProfilePatch,
  type ContactMethod,
  type MePatch,
  useAccountProfile,
  useProfileImageUrl,
  useStartPhoneVerification,
  useUpdateAccountProfile,
  useUpdateActorProfile,
  useUpdateMe,
  useUploadProfileImage,
  useVerifyPhoneCode,
} from '../../lib/api/account'
import { useAuth } from '../../lib/auth'
import { useT } from '../../lib/i18n/use-t'

export const Route = createFileRoute('/account/profile')({
  component: AccountProfilePage,
})

/**
 * Suggested chip options for languages + accents. Codes match the
 * `LANGUAGE_OPTIONS` / `ACCENT_OPTIONS` keys in `@ohear/shared/copy/profile`
 * — the labels here are EN-only because the rest of this surface is also
 * EN. A multi-locale renderer would resolve via `copy.profile` + the
 * active `Locale`.
 */
const LANGUAGE_CHIPS: readonly ChipOption[] = [
  { value: 'en', label: 'English' },
  { value: 'es', label: 'Spanish' },
  { value: 'zh', label: 'Chinese' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'it', label: 'Italian' },
  { value: 'ru', label: 'Russian' },
  { value: 'ar', label: 'Arabic' },
  { value: 'hi', label: 'Hindi' },
]

const ACCENT_CHIPS: readonly ChipOption[] = [
  { value: 'general_american', label: 'General American' },
  { value: 'rp', label: 'British (RP)' },
  { value: 'cockney', label: 'British (Cockney)' },
  { value: 'australian', label: 'Australian' },
  { value: 'irish', label: 'Irish' },
  { value: 'scottish', label: 'Scottish' },
  { value: 'southern_us', label: 'US Southern' },
  { value: 'new_york', label: 'New York' },
  { value: 'spanish_castilian', label: 'Spanish (Castilian)' },
  { value: 'spanish_la', label: 'Spanish (LatAm)' },
  { value: 'mandarin_beijing', label: 'Mandarin (Beijing)' },
  { value: 'cantonese', label: 'Cantonese' },
]

interface ContactForm {
  phone: string
  preferred_contact_method: ContactMethod
  marketing_opt_in: boolean
}

interface ActorExtForm {
  pronouns: string
  bio: string
  years_experience: string
  languages: string[]
  accents: string[]
  union_status: UnionStatus | null
  union_id: string
  rate_per_line: string
  willing_to_travel: boolean
  home_studio_description: string
}

function AccountProfilePage() {
  const t = useT()
  const { user, signOut } = useAuth()
  const profile = useAccountProfile()
  const update = useUpdateAccountProfile()
  const updateMe = useUpdateMe()
  const updateActor = useUpdateActorProfile()
  const startVerify = useStartPhoneVerification()
  const verifyCode = useVerifyPhoneCode()
  const uploadImage = useUploadProfileImage()
  const profileImageUrl = useProfileImageUrl(profile.data?.profile_image_r2_key)
  const toast = useToast()

  const [form, setForm] = useState<AccountProfileUpdate>({})
  const [contact, setContact] = useState<ContactForm>({
    phone: '',
    preferred_contact_method: 'email',
    marketing_opt_in: false,
  })
  const [actorExt, setActorExt] = useState<ActorExtForm>({
    pronouns: '',
    bio: '',
    years_experience: '',
    languages: ['en'],
    accents: [],
    union_status: null,
    union_id: '',
    rate_per_line: '',
    willing_to_travel: false,
    home_studio_description: '',
  })
  const [verifyOpen, setVerifyOpen] = useState(false)

  // Hydrate forms from the loaded profile on first render. Don't overwrite
  // an in-progress edit on subsequent profile refetches.
  useEffect(() => {
    if (!profile.data) return
    setForm((cur) =>
      Object.keys(cur).length === 0
        ? {
            legal_name: profile.data!.legal_name ?? '',
            stage_name: profile.data!.stage_name ?? '',
            timezone: profile.data!.timezone ?? '',
            voice_profile: profile.data!.actor_profile?.voice_profile ?? '',
            demo_url: profile.data!.actor_profile?.demo_url ?? '',
          }
        : cur,
    )
  }, [profile.data])

  useEffect(() => {
    if (!profile.data) return
    setContact({
      phone: profile.data.phone ?? '',
      preferred_contact_method: profile.data.preferred_contact_method ?? 'email',
      marketing_opt_in: profile.data.marketing_opt_in ?? false,
    })
  }, [profile.data])

  useEffect(() => {
    const ap = profile.data?.actor_profile
    if (!ap) return
    setActorExt({
      pronouns: ap.pronouns ?? '',
      bio: ap.bio ?? '',
      years_experience:
        ap.years_experience !== null && ap.years_experience !== undefined
          ? String(ap.years_experience)
          : '',
      languages: ap.languages ? [...ap.languages] : ['en'],
      accents: ap.accents ? [...ap.accents] : [],
      union_status: (ap.union_status ?? null) as UnionStatus | null,
      union_id: ap.union_id ?? '',
      rate_per_line:
        ap.rate_per_line_cents !== null && ap.rate_per_line_cents !== undefined
          ? (ap.rate_per_line_cents / 100).toFixed(2)
          : '',
      willing_to_travel: ap.willing_to_travel ?? false,
      home_studio_description: ap.home_studio_description ?? '',
    })
  }, [profile.data])

  if (!user) {
    return (
      <Layout>
        <Card>
          <p className="m-0">{t(copy.common.SIGN_IN_REQUIRED.profile)}</p>
        </Card>
      </Layout>
    )
  }

  if (profile.isLoading || !profile.data) {
    return (
      <Layout isActor={false}>
        <div className="flex items-center gap-2 text-[var(--text-2)]">
          <Spinner /> Loading profile...
        </div>
      </Layout>
    )
  }

  const isActor = profile.data.primary_role === 'actor'

  const submitCore = (e: React.FormEvent) => {
    e.preventDefault()
    update.mutate(form, {
      onSuccess: () => toast.show({ severity: 'success', title: 'Profile saved' }),
      onError: (err) =>
        toast.show({
          severity: 'error',
          title: 'Could not save profile',
          description: err instanceof Error ? err.message : 'Unknown error',
        }),
    })
  }

  const saveContact = () => {
    const patch: MePatch = {
      // Only send phone if it changed (avoids resetting verified flag on
      // every save).
      preferred_contact_method: contact.preferred_contact_method,
      marketing_opt_in: contact.marketing_opt_in,
    }
    if ((profile.data?.phone ?? '') !== contact.phone) {
      patch.phone = contact.phone || null
    }
    updateMe.mutate(patch, {
      onSuccess: () => toast.show({ severity: 'success', title: 'Contact info saved' }),
      onError: (err) =>
        toast.show({
          severity: 'error',
          title: 'Could not save contact info',
          description: err instanceof Error ? err.message : 'Unknown error',
        }),
    })
  }

  const saveActorExt = () => {
    const patch: ActorProfilePatch = {
      pronouns: actorExt.pronouns || null,
      bio: actorExt.bio || null,
      years_experience: actorExt.years_experience ? Number(actorExt.years_experience) : null,
      languages: actorExt.languages,
      accents: actorExt.accents,
      union_status: actorExt.union_status,
      union_id: actorExt.union_id || null,
      rate_per_line_cents: actorExt.rate_per_line
        ? Math.round(Number(actorExt.rate_per_line) * 100)
        : null,
      willing_to_travel: actorExt.willing_to_travel,
      home_studio_description: actorExt.home_studio_description || null,
    }
    updateActor.mutate(patch, {
      onSuccess: () => toast.show({ severity: 'success', title: 'Performer details saved' }),
      onError: (err) =>
        toast.show({
          severity: 'error',
          title: 'Could not save performer details',
          description: err instanceof Error ? err.message : 'Unknown error',
        }),
    })
  }

  const handleImageUpload = async (file: File) => {
    const r2Key = await uploadImage.mutateAsync(file)
    await updateMe.mutateAsync({ profile_image_r2_key: r2Key })
    toast.show({ severity: 'success', title: 'Photo updated' })
  }

  const handleImageRemove = async () => {
    await updateMe.mutateAsync({ profile_image_r2_key: null })
    toast.show({ severity: 'success', title: 'Photo removed' })
  }

  return (
    <Layout isActor={isActor} profile={profile.data} onSignOut={() => void signOut()}>
      <form onSubmit={submitCore} className="flex flex-col gap-4 max-w-2xl">
        <Field label="Real name (for contracts)" hint="Locks once a contract is signed.">
          <Input
            value={form.legal_name ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, legal_name: e.target.value }))}
            autoComplete="name"
          />
        </Field>
        <Field label="Stage name" hint="Shown on credits and casting cards.">
          <Input
            value={form.stage_name ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, stage_name: e.target.value }))}
          />
        </Field>
        <Field label="Email" hint="Set at sign-in. Contact support to change.">
          <Input value={profile.data.email} readOnly disabled />
        </Field>
        <Field label="Timezone" hint="IANA name, e.g. America/Los_Angeles.">
          <Input
            value={form.timezone ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))}
            placeholder={Intl.DateTimeFormat().resolvedOptions().timeZone}
          />
        </Field>

        {isActor ? (
          <>
            <Field label="Voice profile" hint="A short blurb shown to producers.">
              <Textarea
                value={form.voice_profile ?? ''}
                rows={3}
                onChange={(e) => setForm((f) => ({ ...f, voice_profile: e.target.value }))}
              />
            </Field>
            <Field label="Demo URL" hint="Public link (SoundCloud, Vimeo, etc).">
              <Input
                type="url"
                value={form.demo_url ?? ''}
                onChange={(e) => setForm((f) => ({ ...f, demo_url: e.target.value }))}
              />
            </Field>
          </>
        ) : null}

        <div className="flex items-center justify-end">
          <Button type="submit" variant="primary" loading={update.isPending}>
            Save profile
          </Button>
        </div>
      </form>

      {/* ── Contact section (PII added in 0009) ──────────────────────────── */}
      <Card title="Contact" className="mt-6 max-w-2xl">
        <div className="flex flex-col gap-4">
          <Field
            label="Phone number"
            hint="For session reminders and urgent notes. Verified before we use it."
          >
            <PhoneInput
              value={contact.phone}
              onChange={(next) => setContact((c) => ({ ...c, phone: next }))}
              verified={profile.data.phone_verified && profile.data.phone === contact.phone}
              onRequestVerify={() => {
                // Save first if the typed phone differs from what's on file —
                // verification reads from users.phone server-side.
                if ((profile.data?.phone ?? '') !== contact.phone) {
                  updateMe.mutate(
                    { phone: contact.phone || null },
                    {
                      onSuccess: () => setVerifyOpen(true),
                      onError: (err) =>
                        toast.show({
                          severity: 'error',
                          title: 'Could not save phone',
                          description: err instanceof Error ? err.message : 'Unknown error',
                        }),
                    },
                  )
                } else {
                  setVerifyOpen(true)
                }
              }}
            />
          </Field>

          <fieldset className="flex flex-col gap-1 border-0 p-0 m-0">
            <legend className="text-[13px] font-medium text-[var(--text)] mb-1 p-0">
              How should we reach you?
            </legend>
            <div className="flex flex-wrap gap-2">
              {(['email', 'phone', 'sms'] as const).map((m) => {
                const checked = contact.preferred_contact_method === m
                const label =
                  m === 'email' ? 'Email' : m === 'phone' ? 'Phone call' : 'Text message'
                return (
                  <label
                    key={m}
                    className={`inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-[13px] cursor-pointer ${
                      checked
                        ? 'border-[var(--accent,#3b82f6)] bg-[var(--accent-soft,#eef4ff)]'
                        : 'border-[var(--border)]'
                    }`}
                  >
                    <input
                      type="radio"
                      name="contact-method"
                      value={m}
                      checked={checked}
                      onChange={() => setContact((c) => ({ ...c, preferred_contact_method: m }))}
                      className="sr-only"
                    />
                    {label}
                  </label>
                )
              })}
            </div>
            <p className="text-[12px] text-[var(--text-2)] m-0 mt-1">
              We default to email; switch if SMS is faster for you.
            </p>
          </fieldset>

          <Checkbox
            label="Send me product updates"
            description="Optional. Roughly one short email a month, no spam."
            checked={contact.marketing_opt_in}
            onChange={(e) => setContact((c) => ({ ...c, marketing_opt_in: e.target.checked }))}
          />

          <div className="flex items-center justify-end">
            <Button
              type="button"
              variant="primary"
              onClick={saveContact}
              loading={updateMe.isPending}
            >
              Save contact info
            </Button>
          </div>
        </div>
      </Card>

      {/* ── Profile photo ─────────────────────────────────────────────────── */}
      <Card title="Profile photo" className="mt-6 max-w-2xl">
        <ImageUpload
          currentUrl={profileImageUrl.data ?? null}
          displayName={profile.data.stage_name ?? profile.data.legal_name ?? profile.data.email}
          onUpload={handleImageUpload}
          onRemove={profile.data.profile_image_r2_key ? handleImageRemove : undefined}
        />
      </Card>

      {/* ── Performer details (actor only) ───────────────────────────────── */}
      {isActor ? (
        <Card title="Performer details" className="mt-6 max-w-2xl">
          <div className="flex flex-col gap-4">
            <Field label="Pronouns" hint="Free-form, e.g. she/her, they/them.">
              <Input
                value={actorExt.pronouns}
                onChange={(e) => setActorExt((a) => ({ ...a, pronouns: e.target.value }))}
                placeholder="they/them"
              />
            </Field>

            <Field label="Short bio" hint="A short paragraph producers see while casting.">
              <Textarea
                rows={4}
                value={actorExt.bio}
                onChange={(e) => setActorExt((a) => ({ ...a, bio: e.target.value }))}
              />
            </Field>

            <Field label="Years of experience">
              <Input
                type="number"
                inputMode="numeric"
                min={0}
                max={120}
                value={actorExt.years_experience}
                onChange={(e) => setActorExt((a) => ({ ...a, years_experience: e.target.value }))}
              />
            </Field>

            <Field label="Languages I perform in">
              <ChipMultiSelect
                value={actorExt.languages}
                onChange={(next) => setActorExt((a) => ({ ...a, languages: next }))}
                options={LANGUAGE_CHIPS}
                placeholder="Add a language…"
              />
            </Field>

            <Field label="Accents I can do">
              <ChipMultiSelect
                value={actorExt.accents}
                onChange={(next) => setActorExt((a) => ({ ...a, accents: next }))}
                options={ACCENT_CHIPS}
                placeholder="Add an accent…"
              />
            </Field>

            <Field label="Union status">
              <UnionStatusGroup
                status={actorExt.union_status}
                unionId={actorExt.union_id}
                onStatusChange={(s) => setActorExt((a) => ({ ...a, union_status: s }))}
                onUnionIdChange={(v) => setActorExt((a) => ({ ...a, union_id: v }))}
                unionIdHint="Required for union projects. Kept private from other actors."
              />
            </Field>

            <Field
              label="My rate per line (USD)"
              hint="Your asking rate. The actual contract is negotiated per project."
            >
              <div className="flex items-center gap-2">
                <span className="text-[var(--text-2)]">$</span>
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  inputMode="decimal"
                  value={actorExt.rate_per_line}
                  onChange={(e) => setActorExt((a) => ({ ...a, rate_per_line: e.target.value }))}
                  placeholder="2.50"
                />
              </div>
            </Field>

            <Checkbox
              label="Willing to travel for in-person sessions"
              checked={actorExt.willing_to_travel}
              onChange={(e) => setActorExt((a) => ({ ...a, willing_to_travel: e.target.checked }))}
            />

            <Field
              label="My home studio"
              hint="Mic, interface, and room treatment — helps producers gauge fit."
            >
              <Textarea
                rows={3}
                value={actorExt.home_studio_description}
                onChange={(e) =>
                  setActorExt((a) => ({ ...a, home_studio_description: e.target.value }))
                }
              />
            </Field>

            <div className="flex items-center justify-end">
              <Button
                type="button"
                variant="primary"
                onClick={saveActorExt}
                loading={updateActor.isPending}
              >
                Save performer details
              </Button>
            </div>
          </div>
        </Card>
      ) : null}

      {isActor && profile.data.actor_profile ? (
        <Card title="Participating projects" className="mt-6">
          {(profile.data.actor_profile.participating_projects ?? []).length === 0 ? (
            <p className="text-[13px] text-[var(--text-2)] m-0">You're not on any projects yet.</p>
          ) : (
            <ul className="m-0 p-0 list-none flex flex-col gap-2">
              {(profile.data.actor_profile.participating_projects ?? []).map((p) => (
                <li
                  key={p.project_id}
                  className="flex items-center gap-2 text-[13px] border-b border-[var(--border)] pb-2 last:border-b-0 last:pb-0"
                >
                  <span className="font-medium">{p.project_name}</span>
                  <span className="text-[var(--text-2)]">- {p.character_display_name}</span>
                  <span className="text-[var(--text-3)] ml-auto">{p.organization_name}</span>
                  <Badge
                    variant={
                      p.status === 'completed' ? 'green' : p.status === 'paused' ? 'amber' : 'blue'
                    }
                  >
                    {p.status.replace('_', ' ')}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>
      ) : null}

      <PhoneVerifyModal
        open={verifyOpen}
        phone={contact.phone || profile.data.phone || null}
        onSendCode={async () => {
          const r = await startVerify.mutateAsync()
          return { expires_in: r.expires_in }
        }}
        onVerify={async (code) => {
          await verifyCode.mutateAsync(code)
        }}
        onClose={() => setVerifyOpen(false)}
        devHint="Test mode: any number; the code is 000000."
      />
    </Layout>
  )
}

function Layout({
  children,
  isActor = false,
  profile,
  onSignOut,
}: {
  children: React.ReactNode
  isActor?: boolean
  profile?: AccountProfile
  onSignOut?: () => void
}) {
  return (
    <section className="flex flex-col gap-4">
      <AccountHeader profile={profile} onSignOut={onSignOut} />
      <AccountTabs active="profile" isActor={isActor}>
        {children}
      </AccountTabs>
    </section>
  )
}

export function AccountHeader({
  profile,
  onSignOut,
}: {
  profile?: AccountProfile
  onSignOut?: () => void
}) {
  const name = profile?.stage_name ?? profile?.legal_name ?? profile?.email ?? '?'
  return (
    <header className="flex items-center gap-4 border-b border-[var(--border)] pb-4">
      <Avatar src={profile?.avatar_url ?? undefined} name={name} size="lg" />
      <div className="flex-1 min-w-0">
        <div className="text-[12px] uppercase tracking-wide text-[var(--text-3)]">My account</div>
        <h1 className="text-[20px] font-semibold tracking-tight m-0 truncate text-[var(--text)]">
          {name}
        </h1>
        <div className="mt-1 flex items-center gap-2">
          {profile?.primary_role ? <Badge variant="blue">{profile.primary_role}</Badge> : null}
          {profile?.email ? (
            <span className="text-[12px] text-[var(--text-2)] truncate">{profile.email}</span>
          ) : null}
        </div>
      </div>
      {onSignOut ? (
        <Button variant="ghost" onClick={onSignOut}>
          Sign out
        </Button>
      ) : null}
    </header>
  )
}

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="flex flex-col gap-1 text-[13px]">
      <span className="font-medium text-[var(--text)]">{label}</span>
      {children}
      {hint ? <span className="text-[12px] text-[var(--text-2)]">{hint}</span> : null}
    </label>
  )
}
